import Button from '@berlitz/button'
import { Col, Grid, Row } from 'react-styled-flexboxgrid'
import styled, { css } from 'styled-components'

import { radiusXxxl } from '@utils/constants'
import { _attention } from '@utils/mixins'
import { H1 } from '@berlitz/globals'

export const TooltipRow = styled(Row)`
  display: inline-block;
  padding: 0;
  margin: 0;
`

export const StyledGrid = styled(Grid)`
  padding: 0 ${({ theme }) => theme.space.sm};
`

export const CardIconCol = styled(Col)`
  padding: 0;
  text-align: left;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    text-align: center;
  }

  .cardicon-row {
    padding: ${({ theme }) => theme.space.md};
  }

  p {
    font-size: ${({ theme }) => theme.fontSize.xxxs};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    color: ${({ theme }) => theme.palette.blueGray60};
  }
`

export const StyledIcon = styled.span`
  span {
    width: ${({ theme }) => theme.layout.xs};
    height: ${({ theme }) => theme.layout.xs};
    font-size: ${({ theme }) => theme.fontSize.xs};
    border-radius: ${radiusXxxl};
    background-color: ${({ theme }) => theme.palette.green100};
    display: inline-block;
    vertical-align: middle;
    margin-left: ${({ theme }) => theme.space.xs};

    svg {
      display: block;
      margin: ${({ theme }) => theme.space.xxs} auto auto;
    }
  }
`

const PastDateBG = css`
  background-color: ${({ theme }) => theme.palette.blueGray50};
`

const PendingBG = css`
  background-color: ${({ theme }) => theme.palette.purple100};
`

const CancelledBG = css`
  background-color: ${({ theme }) => theme.palette.danger100};
`

export const Status = styled.div<{ pastDate?: boolean; pending?: boolean; cancelled?: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.xxs};
  display: inline-block;
  padding: 0 ${({ theme }) => theme.space.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semiBold};

  ${({ theme, pending }) =>
    pending &&
    css`
      margin-left: ${theme.space.md};
    `}

  ${StyledIcon} {
    span {
      ${({ pending }) => pending && PendingBG}

      ${({ cancelled }) => cancelled && CancelledBG}

      ${({ pastDate }) => pastDate && PastDateBG}
    }
  }
`

export const ContentCol = styled(Col)`
  padding: ${({ theme }) => theme.space.md};
  border: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    border-left: 1px solid ${({ theme }) => theme.palette.gray40};
  }
`

export const InfoCol = styled(Col)`
  margin-top: ${({ theme }) => theme.space.sm};
  color: ${({ theme }) => theme.palette.charcoal};

  div {
    &:last-child span {
      margin-right: ${({ theme }) => theme.space.sm};
    }
  }

  span {
    display: inline-block;
    margin-right: ${({ theme }) => theme.space.xs};
    color: ${({ theme }) => theme.palette.blueGray50};
  }

  a {
    display: inline-block;
    cursor: pointer;
    text-decoration: underline;
    vertical-align: top;

    &.unconfirmed {
      color: ${({ theme }) => theme.palette.charcoal};
      text-decoration: none;
      cursor: auto;

      span {
        padding: 0;
        margin: 0;

        &:first-child {
          color: ${({ theme }) => theme.colors.brandSecondaryContrast};
        }

        &:last-child {
          color: ${({ theme }) => theme.colors.brandPrimary};
        }
      }
    }

    span {
      margin-left: ${({ theme }) => theme.space.xs};
      cursor: pointer;
    }
  }
`

const SuccessBtnCSS = css`
  background-color: ${({ theme }) => theme.palette.green100};
  color: ${({ theme }) => theme.palette.charcoal};
  ${({ theme }) =>
    _attention(css`
      background-color: ${theme.palette.green100};
      transition: 0.4s;
      opacity: 0.8;
    `)};
`

export const StyledButton = styled(Button)<{ success?: boolean }>`
  white-space: nowrap;
  padding: ${({ theme }) => `${theme.space.xs} ${theme.space.lg}`};
  font-size: ${({ theme }) => theme.fontSize.xxs};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  border-radius: ${({ theme }) => theme.radius.md};
  border: none;
  ${({ success }) => (success ? SuccessBtnCSS : null)};
`

export const LessonTypeBox = styled.div`
  .icon {
    background-color: ${({ theme }) => theme.palette.powder};
    width: 56px;
    height: 56px;
    border-radius: ${({ theme }) => theme.layout.xxxl};
    margin: auto ${({ theme }) => theme.layout.xxs} auto auto;
    color: ${({ theme }) => theme.palette.purple60};
    display: inline-block;

    box-shadow: 0 0 9px 0 rgba(0, 0, 0, 0.14);

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      display: block;
      margin: auto;
    }

    svg {
      margin: ${({ theme }) => theme.space.md} auto auto ${({ theme }) => theme.space.md};

      @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        margin-left: auto;
      }
    }
  }

  p {
    display: inline-block;
    vertical-align: top;
    margin-top: 20px;

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      margin-top: ${({ theme }) => theme.space.xs};
    }
  }
`

export const CardBox = styled.div<{ pastDate?: boolean; isGroup?: boolean; isFirst?: boolean; isLast?: boolean }>`
  background-color: ${({ theme }) => theme.palette.white};
  margin-bottom: ${({ theme, isGroup }) => (isGroup ? 0 : theme.space.md)};
  border-top: ${({ theme, isGroup, isFirst }) => (isGroup && !isFirst ? `3px solid ${theme.palette.gray40}` : 0)};
  ${({ theme, isFirst, isLast }) =>
    css`
      border-radius: ${`${isFirst ? theme.radius.md : 0} ${isFirst ? theme.radius.md : 0} ${
        isLast ? theme.radius.md : 0
      } ${isLast ? theme.radius.md : 0}`};
    `};

  box-shadow: ${({ isFirst, isGroup }) => `0 ${isGroup && !isFirst ? '3px' : 0} 9px 0 rgba(0, 0, 0, 0.14)`};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};

  ${({ theme, pastDate }) =>
    pastDate &&
    css`
      background-color: ${theme.palette.gray10};
      border: ${`${theme.space.xxxs} dashed ${theme.palette.blueGray20}`};
      box-shadow: none;

      ${LessonTypeBox} {
        .icon {
          color: ${theme.palette.gray100};
        }
      }
    `};

  .text-right {
    text-align: right;
  }

  .status {
    font-size: ${({ theme }) => theme.fontSize.xxs};
    display: inline-block;
    padding: 0 ${({ theme }) => theme.space.xs};
  }

  .action-btns {
    padding: ${({ theme }) => `${theme.space.xs} ${theme.space.md}`};
    border-top: 1px solid ${({ theme }) => theme.palette.gray40};
    border-bottom: 1px solid ${({ theme }) => theme.palette.gray40};
  }

  .link-btn {
    display: block;
    text-align: right;
    text-decoration: underline;
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    font-size: ${({ theme }) => theme.fontSize.xxs};
    margin-top: ${({ theme }) => theme.space.xs};
    cursor: pointer;

    &.inline {
      display: inline-block;
      margin-left: ${({ theme }) => theme.space.xs};
    }
  }

  .files {
    display: inline-block;
    margin: ${({ theme }) => `0 ${theme.space.sm}`};
    vertical-align: middle;
    cursor: pointer;

    &:nth-child(2) {
      margin-right: ${({ theme }) => theme.space.lg};
    }

    &.disabled {
      cursor: auto;
      color: ${({ theme }) => theme.palette.blueGray50};
    }
  }
`

export const ProgramDiv = styled.div`
  display: inline-flex;
  align-items: baseline;
  background-color: ${({ theme }) => theme.colors.ui09};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => `${theme.space.xxs} ${theme.space.xxs}`};
  font-size: ${({ theme }) => theme.fontSize.xxs};
  line-height: 1.3;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
  }
`

export const DateDiv = styled.div`
  color: ${({ theme }) => theme.palette.blueGray50};
  font-size: ${({ theme }) => theme.fontSize.xxs};
  line-height: 1.3;
`

export const IconItemWrapper = styled.div`
  display: flex;
  align-items: center;
  &:not(:first-child) {
    margin-top: ${({ theme }) => theme.space.xxs};
  }
`

export const ActionWrapper = styled.div`
  display: inline-flex;
  a > span.files {
    margin: 0 ${({ theme }) => theme.space.xs};
  }
`

export const GroupButtonsWrapper = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.space.sm};
  right: ${({ theme }) => theme.space.sm};
  text-align: right;
  overflow: hidden;
  & > a {
    margin: ${({ theme }) => theme.space.xs} 0;
    display: block;
    cursor: pointer;
    text-decoration: underline;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    text-align: left;
    & > button {
      margin-left: ${({ theme }) => theme.space.sm};
    }
    & > a {
      display: inline-block;
    }
  }
`

export const Breaker = styled.span`
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
    height: ${({ theme }) => theme.space.xs};
  }
`

export const GroupBreaker = styled.span`
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: block;
    height: ${({ theme }) => theme.space.xs};
  }
`

export const SelfTechCheckWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  & > div {
    margin-bottom: ${({ theme }) => theme.space.xs};
  }
`

export const DisabledLink = styled.a`
  cursor: not-allowed !important;
  color: ${({ theme }) => theme.palette.blueGray50} !important;
`

export const RebookH1 = styled(H1)`
  ${({ theme }) => `
    font-size: ${theme.fontSize.xl};
    font-weight: ${theme.fontWeight.bold};
    font-stretch: normal;
    font-style: normal;
    line-height: 1.19;
    letter-spacing: 1.12px;
    text-align: center;
  `}
`
